import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pb from '../lib/pocketbase';
import { useAuth } from '../contexts/AuthContext';

const AuthModal = ({ isOpen, onClose, mode, onModeChange }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const { login, register } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
    setResetMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (mode === 'login') {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          onClose();
          setFormData({ email: '', password: '', passwordConfirm: '', name: '' });
          setResetMessage('');
        } else {
          setError(result.error);
        }
      } else if (mode === 'reset') {
        // Envoi du lien de réinitialisation de mot de passe
        if (!formData.email) {
          setError('Veuillez saisir votre email.');
          setLoading(false);
          return;
        }
        try {
          setResetLoading(true);
          await pb.collection('users').requestPasswordReset(formData.email);
          setError('');
          setSuccess('Un email de réinitialisation vous a été envoyé si cette adresse existe.');
        } catch (e) {
          console.error('Erreur reset mot de passe:', e);
          setError("Impossible d'envoyer l'email de réinitialisation. Réessayez plus tard.");
        } finally {
          setResetLoading(false);
        }
      } else {
        if (formData.password !== formData.passwordConfirm) {
          setError('Les mots de passe ne correspondent pas');
          setLoading(false);
          return;
        }

        const result = await register(
          formData.email, 
          formData.password, 
          formData.passwordConfirm, 
          formData.name
        );
        
        if (result.success) {
          setSuccess(result.message);
          setFormData({ email: '', password: '', passwordConfirm: '', name: '' });
          // Ne pas fermer le modal immédiatement pour que l'utilisateur voie le message
          setTimeout(() => {
            onClose();
            setSuccess('');
          }, 3000);
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    }
    
    setLoading(false);
  };

  const handleClose = () => {
    setFormData({ email: '', password: '', passwordConfirm: '', name: '' });
    setError('');
    setSuccess('');
    setResetMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h2>
            {mode === 'login' && 'Connexion'}
            {mode === 'register' && 'Inscription'}
            {mode === 'reset' && 'Réinitialiser le mot de passe'}
          </h2>
          <button className="close-btn" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'register' && (
            <div className="form-group">
              <label htmlFor="name">Nom complet</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Votre nom complet"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="votre@email.com"
            />
          </div>

          {mode !== 'reset' && (
            <div className="form-group">
              <label htmlFor="password">Mot de passe</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="••••••••"
                minLength={6}
              />
            </div>
          )}

          {mode === 'register' && (
            <div className="form-group">
              <label htmlFor="passwordConfirm">Confirmer le mot de passe</label>
              <input
                type="password"
                id="passwordConfirm"
                name="passwordConfirm"
                value={formData.passwordConfirm}
                onChange={handleChange}
                required
                placeholder="••••••••"
                minLength={6}
              />
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          {resetMessage && !error && !success && (
            <div className="info-message">{resetMessage}</div>
          )}

          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading
              ? 'Chargement...'
              : mode === 'login'
                ? 'Se connecter'
                : mode === 'register'
                  ? "S'inscrire"
                  : 'Envoyer le lien de réinitialisation'}
          </button>
        </form>

        <div className="auth-switch">
          {mode === 'login' ? (
            <>
              <p>
                Pas encore de compte ?{' '}
                <button 
                  type="button" 
                  className="switch-btn"
                  onClick={() => {
                    handleClose();
                    navigate('/inscription');
                  }}
                >
                  S'inscrire
                </button>
              </p>
              
              <p style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="switch-btn"
                  onClick={() => {
                    setError('');
                    setSuccess('');
                    setResetMessage('');
                    onModeChange('reset');
                  }}
                >
                  Mot de passe oublié ?
                </button>
              </p>
            </>
          ) : mode === 'register' ? (
            <p>
              Déjà un compte ?{' '}
              <button 
                type="button" 
                className="switch-btn"
                onClick={() => onModeChange('login')}
              >
                Se connecter
              </button>
            </p>
          ) : (
            <p>
              Vous vous souvenez de votre mot de passe ?{' '}
              <button
                type="button"
                className="switch-btn"
                onClick={() => {
                  setError('');
                  setSuccess('');
                  setResetMessage('');
                  onModeChange('login');
                }}
              >
                Revenir à la connexion
              </button>
            </p>
          )}
        </div>

        <style jsx>{`
          .auth-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2000;
            padding: 20px;
          }

          .auth-modal {
            background: white;
            border-radius: 12px;
            width: 100%;
            max-width: 400px;
            max-height: 90vh;
            overflow-y: auto;
            overflow-x: hidden;
            word-break: break-word;
            -webkit-overflow-scrolling: touch;
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
            animation: modalAppear 0.3s ease-out;
          }

          @keyframes modalAppear {
            from {
              opacity: 0;
              transform: translateY(-50px) scale(0.9);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          .auth-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 24px 0;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 24px;
          }

          .auth-modal-header h2 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .close-btn {
            background: none;
            border: none;
            cursor: pointer;
            color: #6b7280;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: #f3f4f6;
            color: #374151;
          }

          .auth-form {
            padding: 0 24px 24px;
          }

          .form-group {
            margin-bottom: 20px;
          }

          .form-group label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
            color: #374151;
            font-size: 0.9rem;
          }

          .form-group input {
            width: 100%;
            padding: 12px 16px;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            font-size: 1rem;
            transition: all 0.2s ease;
            box-sizing: border-box;
          }

          .form-group input:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .error-message {
            background: #fef2f2;
            color: #dc2626;
            padding: 12px 16px;
            border-radius: 8px;
            margin-bottom: 20px;
            font-size: 0.9rem;
            border-left: 4px solid #dc2626;
            word-break: break-word;
          }

          .success-message {
            background: #f0fdf4;
            color: #16a34a;
            padding: 12px 16px;
            border-radius: 8px;
            margin-bottom: 20px;
            font-size: 0.9rem;
            border-left: 4px solid #16a34a;
            word-break: break-word;
          }

          .info-message {
            background: #eff6ff;
            color: #1d4ed8;
            padding: 10px 14px;
            border-radius: 8px;
            margin-bottom: 16px;
            font-size: 0.9rem;
            border-left: 4px solid #93c5fd;
            word-break: break-word;
          }

          .submit-btn {
            width: 100%;
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            color: white;
            border: none;
            padding: 14px 24px;
            border-radius: 8px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-bottom: 20px;
          }

          .submit-btn:hover:not(:disabled) {
            background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
          }

          .submit-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          .forgot-btn {
            margin-top: 6px;
            background: none;
            border: none;
            padding: 0;
            color: #3b82f6;
            font-size: 0.85rem;
            cursor: pointer;
            text-decoration: underline;
          }

          .forgot-btn:disabled {
            opacity: 0.6;
            cursor: default;
            text-decoration: none;
          }

          .auth-switch {
            text-align: center;
            padding: 20px 24px 24px;
            border-top: 1px solid #e5e7eb;
            margin-top: 20px;
          }

          .auth-switch p {
            margin: 0;
            color: #6b7280;
            font-size: 0.9rem;
          }

          .switch-btn {
            background: none;
            border: none;
            color: #3b82f6;
            font-weight: 600;
            cursor: pointer;
            text-decoration: underline;
            font-size: 0.9rem;
          }

          .switch-btn:hover {
            color: #1d4ed8;
          }

          @media (max-width: 480px) {
            .auth-modal {
              margin: 10px;
              max-width: none;
              border-radius: 10px;
            }
            
            .auth-modal-header {
              padding: 16px 16px 0;
            }
            
            .auth-form {
              padding: 0 16px 16px;
            }
            
            .auth-switch {
              padding: 16px;
            }
            
            .auth-modal-header h2 { font-size: 1.25rem; }
            .submit-btn { padding: 12px 16px; }
            .form-group input { padding: 10px 12px; }
          }

          @media (max-width: 360px) {
            .auth-modal { margin: 8px; }
            .auth-modal-header { padding: 12px 12px 0; }
            .auth-form { padding: 0 12px 12px; }
            .auth-switch { padding: 12px; }
            .auth-modal-header h2 { font-size: 1.1rem; }
            .submit-btn { padding: 10px 12px; font-size: 0.95rem; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default AuthModal;
